/**
 * Complaint Service
 * Handles complaint data fetching and actions
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { delay } from '../mock/mockData';
import { parseDate, formatDateTime } from '../../utils/dateUtils';
import { getCallSignDisplayName } from '../../utils/generalUtils';
import type {
    Complaint,
    ComplaintDetail,
    ComplaintFilter,
    PaginatedComplaints,
    ComplaintStatus,
    ComplaintCategory,
} from '../../types/complaint';

const mockComplaintDetailOverrides = new Map<string, Partial<ComplaintDetail>>();

const normalizeResolutionAction = (value: unknown): ComplaintDetail['resolutionAction'] => {
    switch (value) {
        case 'WarnUser':
        case 'Warning':
            return 'WarnUser';
        case 'BlockUser':
        case 'Suspension':
        case 'Suspend':
            return 'BlockUser';
        default:
            return undefined;
    }
};

const updateMockComplaintOutcome = (
    complaintId: string,
    status: ComplaintStatus,
    resolutionAction: ComplaintDetail['resolutionAction'],
    notes: string
) => {
    const complaint = mockComplaints.find(c => c.id === complaintId);
    if (!complaint) {
        return;
    }

    const resolvedAt = new Date();
    complaint.status = status;
    mockComplaintDetailOverrides.set(complaintId, {
        resolutionAction,
        resolutionNotes: notes,
        resolvedAt,
        resolvedAtText: formatDateTime(resolvedAt),
        resolvedByEmail: 'admin@mispy.ai',
    });
};

// Mock data for development
const mockComplaints: Complaint[] = [
    {
        id: '1',
        category: 'harassment',
        status: 'pending',
        createdAt: new Date('2026-01-15T10:30:00'),
        reporterName: 'ASSET-10001',
        reporterEmail: 'reporter1@example.com',
        reportedUserName: 'GHOST-10001',
        reportedUserEmail: 'reported1@example.com',
        missionId: 'a1b2c3d4-5678-90ab-cdef-111111111111',
        missionTitle: 'Background Check Mission',
        missionCode: 'MSN-001',
        reasonDetail: 'User sent inappropriate messages during mission.',
    },
    {
        id: '2',
        category: 'inappropriate_content',
        status: 'pending',
        createdAt: new Date('2026-01-14T14:20:00'),
        reporterName: 'ASSET-10002',
        reporterEmail: 'reporter2@example.com',
        reportedUserName: 'GHOST-10002',
        reportedUserEmail: 'reported2@example.com',
        missionId: 'a1b2c3d4-5678-90ab-cdef-222222222222',
        missionTitle: 'Surveillance Task',
        missionCode: 'MSN-002',
        reasonDetail: 'Uploaded inappropriate photos as evidence.',
    },
    {
        id: '3',
        category: 'fraud',
        status: 'resolved',
        createdAt: new Date('2026-01-13T09:15:00'),
        reporterName: 'ASSET-10003',
        reporterEmail: 'reporter3@example.com',
        reportedUserName: 'GHOST-10003',
        reportedUserEmail: 'reported3@example.com',
        missionId: 'a1b2c3d4-5678-90ab-cdef-333333333333',
        missionTitle: 'Investigation Case',
        missionCode: 'MSN-003',
        reasonDetail: 'User provided false information about identity.',
    },
    {
        id: '4',
        category: 'spam',
        status: 'rejected',
        createdAt: new Date('2026-01-12T16:45:00'),
        reporterName: 'ASSET-10004',
        reporterEmail: 'reporter4@example.com',
        reportedUserName: 'GHOST-10004',
        reportedUserEmail: 'reported4@example.com',
        missionId: 'a1b2c3d4-5678-90ab-cdef-444444444444',
        missionTitle: 'Asset Protection',
        missionCode: 'MSN-004',
        reasonDetail: 'Sending promotional messages repeatedly.',
    },
];

const generateMockComplaintDetail = (id: string): ComplaintDetail => {
    const complaint = mockComplaints.find(c => c.id === id) || mockComplaints[0];
    const override = mockComplaintDetailOverrides.get(id);
    const defaultResolvedAt = complaint.status !== 'pending' ? new Date('2026-01-14T12:00:00') : undefined;

    return {
        ...complaint,
        reportedUserId: 'mock-reported-user-id-' + id,
        requestedBlockUser: Math.random() > 0.5,
        resolutionAction: complaint.status === 'resolved' ? 'WarnUser' : undefined,
        resolutionNotes: complaint.status === 'resolved'
            ? 'User has been warned about the behavior.'
            : complaint.status === 'rejected'
                ? 'Complaint rejected due to insufficient evidence.'
                : undefined,
        resolvedAt: defaultResolvedAt,
        resolvedAtText: formatDateTime(defaultResolvedAt),
        resolvedByEmail: complaint.status !== 'pending' ? 'admin@mispy.ai' : undefined,
        ...override,
    };
};

// Transform API response to frontend model
const transformComplaint = (data: any): Complaint => {
    let category = data.category || data.Category || 'other';
    const status = data.status || data.Status || 'pending';
    const createdAt = parseDate(data.createdAt || data.CreatedAt) || new Date();

    // Support PascalCase from API
    if (category === 'InappropriateContent') {
        category = 'inappropriate_content';
    } else {
        category = category.toLowerCase();
    }

    return {
        id: data.id || data.Id || '',
        category: category as ComplaintCategory,
        status: String(status).toLowerCase() as ComplaintStatus,
        createdAt,
        createdAtText: formatDateTime(createdAt),
        reporterName: getCallSignDisplayName(data.reporterName ?? data.ReporterName, 'User'),
        reporterEmail: data.reporterEmail || data.ReporterEmail || '',
        reportedUserName: getCallSignDisplayName(data.reportedUserName ?? data.ReportedUserName, 'User'),
        reportedUserEmail: data.reportedUserEmail || data.ReportedUserEmail || '',
        missionId: data.missionId || data.MissionId || '',
        missionTitle: data.missionTitle || data.MissionTitle || '',
        missionCode: data.missionCode || data.MissionCode || '',
        reasonDetail: data.reasonDetail || data.ReasonDetail,
    };
};

const transformComplaintDetail = (data: any): ComplaintDetail => ({
    ...transformComplaint(data),
    reportedUserId: data.reportedUserId || data.ReportedUserId || '',
    requestedBlockUser: data.requestedBlockUser || data.RequestedBlockUser || false,
    resolutionAction: normalizeResolutionAction(data.resolutionAction || data.ResolutionAction),
    resolutionNotes: data.resolutionNotes || data.ResolutionNotes,
    resolvedAt: parseDate(data.resolvedAt || data.ResolvedAt) || undefined,
    resolvedAtText: formatDateTime(parseDate(data.resolvedAt || data.ResolvedAt)),
    resolvedByEmail: data.resolvedByEmail || data.ResolvedByEmail,
});

export const ComplaintService = {
    /**
     * Get paginated list of complaints
     */
    async getComplaints(
        page: number = 1,
        pageSize: number = 10,
        filter?: ComplaintFilter
    ): Promise<PaginatedComplaints> {
        if (IS_MOCK) {
            await delay(MOCK_DELAY);

            let filtered = [...mockComplaints];

            // Apply status filter
            if (filter?.status && filter.status !== 'all') {
                if (Array.isArray(filter.status)) {
                    filtered = filtered.filter(c => (filter.status as ComplaintStatus[]).includes(c.status));
                } else {
                    filtered = filtered.filter(c => c.status === filter.status);
                }
            }

            // Apply search filter
            if (filter?.search) {
                const searchLower = filter.search.toLowerCase();
                filtered = filtered.filter(c =>
                    c.reporterName.toLowerCase().includes(searchLower) ||
                    c.reporterEmail.toLowerCase().includes(searchLower) ||
                    c.reportedUserName.toLowerCase().includes(searchLower) ||
                    c.reportedUserEmail.toLowerCase().includes(searchLower) ||
                    c.missionTitle.toLowerCase().includes(searchLower) ||
                    c.missionCode.toLowerCase().includes(searchLower)
                );
            }

            const total = filtered.length;
            const totalPages = Math.ceil(total / pageSize);
            const start = (page - 1) * pageSize;
            const data = filtered.slice(start, start + pageSize).map(c => ({
                ...c,
                createdAtText: formatDateTime(c.createdAt),
            }));

            return { data, total, page, pageSize, totalPages };
        }

        // Real API call
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());

        if (filter?.status && filter.status !== 'all') {
            if (Array.isArray(filter.status)) {
                filter.status.forEach(s => params.append('status', s));
            } else {
                params.append('status', filter.status);
            }
        }

        if (filter?.search) {
            params.append('keyword', filter.search);
        }

        const response = await apiService.get<any>(`/admin/compliance/complaints?${params.toString()}`);
        const items = response.data?.items || [];
        const total = response.data?.total || 0;

        return {
            data: items.map(transformComplaint),
            total,
            page,
            pageSize,
            totalPages: Math.ceil(total / pageSize),
        };
    },

    /**
     * Get complaint detail by ID
     */
    async getComplaintDetail(id: string): Promise<ComplaintDetail> {
        if (IS_MOCK) {
            await delay(MOCK_DELAY);
            return generateMockComplaintDetail(id);
        }

        const response = await apiService.get<any>(`/admin/compliance/complaints/${id}`);
        return transformComplaintDetail(response.data);
    },

    /**
     * Issue a warning to a user
     */
    /**
     * Issue a warning to a user
     */
    async issueWarning(complaintId: string, notes: string): Promise<void> {
        if (IS_MOCK) {
            await delay(MOCK_DELAY);
            const complaint = mockComplaints.find(c => c.id === complaintId);
            if (complaint && complaint.reportedUserEmail) {
                const { mockUsers, addWarningToMockUser } = await import('../mock/mockUsers');
                const user = mockUsers.find(u => u.email === complaint.reportedUserEmail);
                if (user) {
                    addWarningToMockUser(user.id, notes);
                }
            }
            console.log('Mock: Warning issued for complaint', complaintId, notes);
            updateMockComplaintOutcome(complaintId, 'resolved', 'WarnUser', notes);
            return;
        }

        await apiService.post('/admin/compliance/warn', {
            id: complaintId,
            notes,
        });
    },

    /**
     * Suspend a user's account
     */
    async suspendUser(complaintId: string, notes: string): Promise<void> {
        if (IS_MOCK) {
            await delay(MOCK_DELAY);
            updateMockComplaintOutcome(complaintId, 'resolved', 'BlockUser', notes);
            console.log('Mock: User suspended for complaint', complaintId, notes);
            return;
        }

        await apiService.post('/admin/compliance/suspend', {
            id: complaintId,
            notes,
        });
    },

    /**
     * Reject a complaint without taking punitive action
     */
    async rejectComplaint(complaintId: string, notes: string): Promise<void> {
        if (IS_MOCK) {
            await delay(MOCK_DELAY);
            updateMockComplaintOutcome(complaintId, 'rejected', undefined, notes);
            console.log('Mock: Complaint rejected', complaintId, notes);
            return;
        }

        await apiService.post('/admin/compliance/reject', {
            id: complaintId,
            notes,
        });
    },
};
